import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export type EditorTab = 'blocks' | 'look' | 'page';

const TABS: readonly EditorTab[] = ['blocks', 'look', 'page'];

/** The history entry of an open row or of the picker carries this mark, so Done can go back. */
interface EditorLayerState {
  editorLayer?: boolean;
}

function isEditorTab(value: string | null): value is EditorTab {
  return value !== null && (TABS as readonly string[]).includes(value);
}

/**
 * The view state of the editor in the URL (design smartlink-editor a1, b1; rule: web navigation must
 * work). `?tab=blocks|look|page` is the panel tab, `?block=<uuid>` the open block row (a sheet on a
 * phone), `?add=1&after=<uuid>` the block picker, `?view=preview` the phone preview. A row and the picker are a new history entry, so
 * Back closes them; Done and the close button go back too when they opened that entry.
 */
export function useEditorUrlState() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const isLayer = (location.state as EditorLayerState | null)?.editorLayer === true;

  const tabParam = params.get('tab');
  const tab: EditorTab = isEditorTab(tabParam) ? tabParam : 'blocks';
  const openBlockUUID = params.get('block');
  const isPickerOpen = params.get('add') === '1';
  const pickerAfter = params.get('after');
  const isPreviewOpen = params.get('view') === 'preview';

  const go = useCallback(
    (change: (next: URLSearchParams) => void, mode: 'push' | 'replace', layer: boolean) => {
      const next = new URLSearchParams(location.search);
      change(next);
      const search = next.toString();
      // eslint-disable-next-line no-void
      void navigate(
        { pathname: location.pathname, search: search === '' ? '' : `?${search}` },
        { replace: mode === 'replace', state: layer ? { editorLayer: true } : null }
      );
    },
    [location.pathname, location.search, navigate]
  );

  const setTab = useCallback(
    (next: EditorTab) => {
      go(
        (query) => {
          query.set('tab', next);
          query.delete('block');
          query.delete('add');
          query.delete('after');
        },
        'replace',
        false
      );
    },
    [go]
  );

  /** Opens a row. A row that is open already gives its place to the new one (one Back closes it). */
  const openBlock = useCallback(
    (widgetUUID: string) => {
      const hasLayer = isLayer && (openBlockUUID !== null || isPickerOpen || isPreviewOpen);
      go(
        (query) => {
          query.set('block', widgetUUID);
          query.delete('add');
          query.delete('after');
          query.delete('view');
          if (query.get('tab') !== null && query.get('tab') !== 'blocks') {
            query.delete('tab');
          }
        },
        hasLayer ? 'replace' : 'push',
        true
      );
    },
    [go, isLayer, isPickerOpen, isPreviewOpen, openBlockUUID]
  );

  /** Closes the open row or the picker: back when the editor opened it, else a replace. */
  const closeLayer = useCallback(() => {
    if (isLayer) {
      // eslint-disable-next-line no-void
      void navigate(-1);
      return;
    }
    go(
      (query) => {
        query.delete('block');
        query.delete('add');
        query.delete('after');
        query.delete('view');
      },
      'replace',
      false
    );
  }, [go, isLayer, navigate]);

  /** Phone width: the page full screen with "Edit" to go back (design a8, `?view=preview`). */
  const openPreview = useCallback(() => {
    go(
      (query) => {
        query.set('view', 'preview');
        query.delete('block');
        query.delete('add');
        query.delete('after');
      },
      'push',
      true
    );
  }, [go]);

  /** Opens the picker. `after` is the block above the new one; null puts it at the end. */
  const openPicker = useCallback(
    (after: string | null) => {
      go(
        (query) => {
          query.set('add', '1');
          if (after === null) {
            query.delete('after');
          } else {
            query.set('after', after);
          }
          query.delete('block');
          query.delete('tab');
        },
        'push',
        true
      );
    },
    [go]
  );

  return {
    tab,
    openBlockUUID,
    isPickerOpen,
    pickerAfter,
    isPreviewOpen,
    setTab,
    openBlock,
    closeLayer,
    openPicker,
    openPreview,
  };
}

export type EditorUrlState = ReturnType<typeof useEditorUrlState>;
